import 'dayjs/locale/sv';

import React, { useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { MediaItem } from '@/components/media-item';
import dayjs from 'dayjs';
import { processField } from '@/utils/textUtils';
import { getMediaThumbnailUrl } from '@/utils/mediaUtils';
import styles from './CollaborationItem.module.css';

import CheckIcon from '../../../../../public/assets/icons/check.svg';
import MoreIcon from '../../../../../public/assets/icons/more.svg';
import MoreBlueIcon from '../../../../../public/assets/icons/not_review.svg';
import { DraftFeedbackModal, FeedbackType } from '../modals/feedback-modal/DraftFeedbackModalModal';

interface DraftHistoryItemProps {
  type: string;
  data: Api.DraftProps;
}

export function DraftHistoryItem({ data, type }: DraftHistoryItemProps) {
  const [activeMediaId, setActiveMediaId] = useState<string | null>(null);
  const [feedbackPopupItemId, setFeedbackPopupItemId] = useState<string | null>(null);
  const [currentItems, setCurrentItems] = useState<(DraftMedia | FeedbackType)[]>([]);

  const { t } = useTranslation();

  function getMediaIcon(status?: 'NOT_REVIEWED' | 'APPROVED' | 'REJECTED', active?: boolean) {
    if (status === 'APPROVED') {
      return <CheckIcon className={`${styles.feedbackIcon} ${active ? styles.active : ''}`} />;
    }
    if (status === 'REJECTED') {
      return <MoreIcon className={`${styles.feedbackIcon} ${active ? styles.active : ''}`} />;
    }
    if (status === 'NOT_REVIEWED') {
      return <MoreBlueIcon className={`${styles.feedbackIcon} ${active ? styles.active : ''}`} />;
    }
    return undefined;
  }

  const handleMouseEnter = (mediaId: string) => {
    setActiveMediaId(mediaId);
  };

  const handleMouseLeave = () => {
    setActiveMediaId(null);
  };

  const mediaItems: (DraftMedia | FeedbackType)[] = [
    ...(data.medias.filter((m) => m.feedback_client?.trim() || m.feedback_cm?.trim()) || []),
    ...(data.feedback_cm?.trim() || data.feedback_client?.trim()
      ? [
          {
            type: 'text',
            media_id: 'general',
            status: (data.date_approved
              ? 'APPROVED'
              : data.date_rejected
                ? 'REJECTED'
                : 'NOT_REVIEWED') as 'REJECTED' | 'APPROVED' | 'NOT_REVIEWED',
            feedback_cm: data.feedback_cm,
            feedback_client: data.feedback_client
          }
        ]
      : []),
    ...(data.captions[0]?.feedback_cm?.trim() || data.captions[0]?.feedback_client?.trim()
      ? [
          {
            type: 'text',
            media_id: 'feedback',
            title: `${processField(type).charAt(0).toUpperCase()}${processField(type).slice(1)} ${t('draft.caption')}`,
            status: data.captions[0].status,
            caption: data.captions[0].caption,
            feedback_cm: data.captions[0].feedback_cm,
            feedback_client: data.captions[0].feedback_client
          }
        ]
      : [])
  ];

  const handleFeedbackClick = (initial: string) => {
    setCurrentItems(mediaItems);
    setFeedbackPopupItemId(initial);
  };

  const handleMediaClick = (initial: DraftMedia) => {
    const arr = data.medias.filter((m) => m.is_raw === initial?.is_raw);
    setCurrentItems(arr);
    setFeedbackPopupItemId(initial.media_id);
  };

  return (
    <div className={styles.draftWrap}>
      <div className={styles.body}>
        <div className={styles.content}>
          <div className={styles.data}>
            <div className={styles.submitted}>
              {`${t('draft.dateSubmitted')}: ${dayjs(data.date_submitted).format('HH:mm, DD.MM.YY')}`}
            </div>

            <div className={styles.dataRow}>
              <div className={styles.rowInfo}>
                <div className={styles.infoTitle}>
                  {t('draft.finalTitle')} <span>*</span>
                </div>
                <div className={styles.infoDescription}>
                  {t('draft.finalSubtitle')
                    .split('\n')
                    .map((line) => (
                      <React.Fragment key={line}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                </div>
              </div>
              <div className={styles.rowContent}>
                {data.medias
                  .filter((media) => !media.is_raw)
                  .map((m) => (
                    <MediaItem
                      key={m.url}
                      src={getMediaThumbnailUrl(m)}
                      topIcon={() =>
                        getMediaIcon(
                          m.status !== 'NOT_REVIEWED' ? m.status : undefined,
                          m.media_id === activeMediaId
                        )
                      }
                      onClick={() => handleMediaClick(m)}
                    />
                  ))}
              </div>
            </div>
            {data && data.captions[0] && data.captions[0].caption && (
              <div className={styles.dataRow}>
                <div className={styles.rowInfo}>
                  <div className={styles.infoTitle}>{t('draft.caption')}</div>
                  <div className={styles.infoDescription}>
                    <Trans
                      i18nKey="draft.captionSubtitle"
                      components={{
                        1: <span />
                      }}
                      values={{ type: processField(type) }}
                    />
                  </div>
                </div>
                <div className={styles.rowContent}>
                  <p
                    className={`${styles.caption} ${
                      data.captions[0].status !== 'NOT_REVIEWED' ? styles.spacing : ''
                    } ${activeMediaId === 'feedback' ? styles.active : ''}`}
                  >
                    {getMediaIcon(
                      data.captions[0].status !== 'NOT_REVIEWED'
                        ? data.captions[0].status
                        : undefined
                    )}
                    {data?.captions[0].caption}
                  </p>
                </div>
              </div>
            )}

            <div className={styles.dataRow}>
              <div className={styles.rowInfo}>
                <div className={styles.infoTitle}>
                  {t('draft.rawTitle')} <span>*</span>
                </div>
                <div className={styles.infoDescription}>{t('draft.rawSubtitle')}</div>
              </div>
              <div className={styles.rowContent}>
                {data.medias
                  .filter((media) => !!media.is_raw)
                  .map((m) => (
                    <MediaItem
                      sizeSm
                      key={m.url}
                      src={getMediaThumbnailUrl(m)}
                      topIcon={() =>
                        getMediaIcon(
                          m.status !== 'NOT_REVIEWED' ? m.status : undefined,
                          m.media_id === activeMediaId
                        )
                      }
                      onClick={() => handleMediaClick(m)}
                    />
                  ))}
              </div>
            </div>
          </div>
          <div className={styles.info}>
            <div className={styles.infoInner}>
              {((data.feedback_cm && data.feedback_cm.trim()) ||
                (data.feedback_client && data.feedback_client.trim())) && (
                <div className={styles.feedback} onClick={() => handleFeedbackClick('general')}>
                  <div className={styles.feedbackTitle}>
                    {getMediaIcon(
                      data.date_approved
                        ? 'APPROVED'
                        : data.date_rejected
                          ? 'REJECTED'
                          : 'NOT_REVIEWED'
                    )}
                    {data.date_approved && (
                      <span className={`${styles.feedbackStatus} ${styles.APPROVED}`}>
                        APPROVED
                      </span>
                    )}
                    {data.date_rejected && (
                      <span className={`${styles.feedbackStatus} ${styles.REJECTED}`}>
                        REJECTED
                      </span>
                    )}
                    <span>General</span>
                  </div>
                  <div className={styles.feedbackBody}>
                    {data.feedback_cm && data.feedback_cm?.trim() && (
                      <>
                        <span className={styles.feedbackUser}>{t('draft.beatly')}: </span>
                        {data.feedback_cm}
                      </>
                    )}
                    {data.feedback_client && data.feedback_client?.trim() && (
                      <>
                        <span className={styles.feedbackUser}>{t('draft.client')}: </span>
                        {data.feedback_client}
                      </>
                    )}
                  </div>
                </div>
              )}
              {((data.captions[0]?.feedback_cm && data.captions[0].feedback_cm.trim()) ||
                (data.captions[0]?.feedback_client && data.captions[0].feedback_client.trim())) && (
                <div
                  className={styles.feedback}
                  onMouseEnter={() => handleMouseEnter('feedback')}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => handleFeedbackClick('feedback')}
                >
                  <div className={styles.feedbackTitle}>
                    {getMediaIcon(data.captions[0].status)}

                    {data.captions[0].status !== 'NOT_REVIEWED' && (
                      <span
                        className={`${styles.feedbackStatus} ${styles[data.captions[0].status]}`}
                      >
                        {data.captions[0].status}
                      </span>
                    )}
                  </div>
                  <div className={styles.feedbackBody}>
                    {data.captions[0].feedback_cm && data.captions[0].feedback_cm?.trim() && (
                      <>
                        <span className={styles.feedbackUser}>{t('draft.beatly')}: </span>
                        {data.captions[0].feedback_cm}
                      </>
                    )}
                    {data.captions[0].feedback_client &&
                      data.captions[0].feedback_client?.trim() && (
                        <>
                          <span className={styles.feedbackUser}>{t('draft.client')}: </span>
                          {data.captions[0].feedback_client}
                        </>
                      )}
                  </div>
                </div>
              )}
              {data.medias
                .filter(
                  (media) =>
                    (media.feedback_cm && media.feedback_cm.trim()) ||
                    (media.feedback_client && media.feedback_client.trim())
                )
                .map((m) => (
                  <div
                    key={m.media_id}
                    className={styles.feedback}
                    onMouseEnter={() => handleMouseEnter(m.media_id)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleFeedbackClick(m.media_id)}
                  >
                    <div className={styles.feedbackTitle}>
                      {getMediaIcon(m.status)}
                      {m.status !== 'NOT_REVIEWED' && (
                        <span className={`${styles.feedbackStatus} ${styles[m.status]}`}>
                          {m.status}
                        </span>
                      )}
                    </div>
                    {m.feedback_cm && (
                      <div className={styles.feedbackBody}>
                        <span className={styles.feedbackUser}>{t('draft.beatly')}: </span>
                        {m.feedback_cm}
                      </div>
                    )}
                    {m.feedback_client && (
                      <div className={styles.feedbackBody}>
                        <span className={styles.feedbackUser}>{t('draft.client')}: </span>
                        {m.feedback_client}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
      {!!feedbackPopupItemId && (
        <DraftFeedbackModal
          items={currentItems}
          initialItem={feedbackPopupItemId}
          onClose={() => {
            setFeedbackPopupItemId(null);
            setCurrentItems([]);
          }}
        />
      )}
    </div>
  );
}
